<template>
    <div class="block raw-development-block adjustment-block" rel="block_raw-development" @click="blockSelected">
        <div class="header-container">
            <label class="lookup_block_name name">Raw Development</label>

            <div class="controls-wrapper">
                <i id="reset-raw-development-btn" class="btn-reset-block material-icons" @click="resetBlock"
                   data-tippy-content="Reset"
                >settings_backup_restore</i>
                <i class="toggle_arrow expand_options material-icons mr-0" data-tippy-content="Expand"
                   v-show="!expanded" @click="expanded = !expanded"
                >expand_more</i>
                <i class="toggle_arrow collapse_options material-icons mr-0" style="display: none;"
                   v-show="expanded" @click="expanded = !expanded"
                   data-tippy-content="Collapse"
                >expand_less</i>
            </div>
        </div>

        <v-expand-transition>
            <div class="body-container" v-show="expanded">
                <div class="block-option">
                    <span>Develop</span>
                    <div class="switch switch-container">
                        <input type="checkbox" v-model="values.develop" @change="developAndRender" />
                        <span class="slider round"></span>
                    </div>
                </div>
                <div class="block-option" :class="{disabled: !values.develop}">
                    <span>Highlight Recovery</span>
                    <div class="switch switch-container">
                        <input type="checkbox" v-model="values.highlightRecovery" :disabled="!values.develop" @change="developAndRender" />
                        <span class="slider round"></span>
                    </div>
                </div>
                <div class="block-option" :class="{disabled: !values.develop}">
                    <span>Linear Response</span>
                    <div class="switch switch-container">
                        <input type="checkbox" v-model="values.linearResponse" :disabled="!values.develop" @change="developAndRender" />
                        <span class="slider round"></span>
                    </div>
                </div>
            </div>
        </v-expand-transition>
    </div>
</template>

<script>

export default {
    data() {
        return {
            worker: null,
            values: {
                develop: false,
                highlightRecovery: false,
                linearResponse: false,
            },

            updating: false,
            expanded: true,
        }
    },

    mounted() {
        this.$root.rawDevelopment = this;
        this.worker = new Worker("assets/vendor/libraw/weblibraw_worker.js");
    },

    watch: {
        values: {
            handler: function() {
                this.$store.commit('UI/SET_RAW_DEVELOPED', this.values.develop);
            },
            deep: true
        }
    },

    methods: {
        getDevelopParameters(options) {
            if (!options.develop && !options.exportingImage) return "-T";

            const linearResponse = options.linearResponse ? "" : "-g 2.222 2.0";
            const highlightRecovery = options.highlightRecovery ? "-H 2" : "";
            const resolution = options.exportingImage ? "-q 0" : "-h";
            const values = `-o 6 -w ${linearResponse} ${highlightRecovery} ${resolution}`;

            return values
        },

        setValues(data) {
            this.values.develop = data.develop;
            this.values.highlightRecovery = data.highlightRecovery;
            this.values.linearResponse = data.linearResponse;
        },

        getValues() {
            return this.values;
        },

        blockSelected(e) {
            blocks.primaryBlockHandler($(e.currentTarget));
        },

        async selectDefaultIDT() {
            await UI.IDT.selectByName('ProPhoto RGB')
        },

        async resetValues() {
            this.values.develop = false;
            this.values.highlightRecovery = false;
            this.values.linearResponse = false;
        },

        imageDataToBlob(imageData) {
            let w = imageData.width;
            let h = imageData.height;
            let canvas = document.getElementById("develop-raw-canvas");
            canvas.width = w;
            canvas.height = h;
            let ctx = canvas.getContext("2d");
            ctx.putImageData(imageData, 0, 0);        // synchronous

            return new Promise((resolve) => {
                canvas.toBlob(resolve); // implied image/png format
            });
        },

        async getThumbnail(imageId) {
            let storeData = await window.uploadImagesStore.getItem(imageId);
            if (!storeData) storeData = await window.imagesStore.getItem(imageId);

            let imageBlob;
            let imageExtension;
            const values = "-T";

            if (storeData.rawPreview) {
                imageBlob = storeData.rawPreview;
                imageExtension = 'jpg';
            } else {
                let result = await this.extractThumbnailFromRaw(storeData.blob, false);

                // for raw files without preview
                if (!result.file_output) result = await this.extractThumbnailFromRaw(storeData.blob, false, "-o 6 -h -w");

                imageBlob = new Blob([result.file_output], {type: "image/jpeg"});
                imageExtension = result.extension.split('.')[1];

                storeData.rawPreview = imageBlob;
                await this.saveToStore(imageId, {...storeData});
            }

            return {
                imageBlob,
                imageExtension,
                values
            };
        },

        async getDevelopedImage(imageId, values, isSavingImage = false) {
            if (!values) {
                values = this.getDevelopParameters({
                    develop: this.values.develop,
                    highlightRecovery: this.values.highlightRecovery,
                    linearResponse: this.values.linearResponse,
                });
            }

            let imageStore = await uploadImagesStore.getItem(imageId);
            if (!imageStore) imageStore = await imagesStore.getItem(imageId);

            let imageBlob;
            let imageExtension;
            let imageData;
            let developedImage;

            const exif = await this.extractExif(imageStore.blob);
            if (exif) this.$store.dispatch('Projects/setExif', exif);

            if (imageStore?.rawDevelopment?.values == values) {
                imageBlob = imageStore.rawDevelopment.blob;
                imageExtension = imageStore.rawDevelopment.type;
            } else {
                if (isSavingImage) {
                    values = this.getDevelopParameters({
                        develop: this.values.develop,
                        highlightRecovery: this.values.highlightRecovery,
                        linearResponse: this.values.linearResponse,
                        exportingImage: true,
                    });
                }

                try {
                    developedImage = await this.extractImageFromRaw(imageStore.blob, values);
                } catch(err) {
                    //
                }

                imageExtension = developedImage.extension.split('.')[1];

                if (developedImage.extension == '.exr') {
                    // exr file
                    imageBlob = new File([developedImage.file_output], `image.${imageExtension}`);
                    developedImage = await loader.convertImage(imageBlob, imageExtension);
                    imageBlob = developedImage.blob;
                } else if (developedImage.preview) {
                    // jpeg file
                    imageExtension = 'jpg';
                    let maxTextureSize = gpu.exportRenderer.capabilities.maxTextureSize;
                    if (developedImage.preview.width > maxTextureSize || developedImage.preview.height > maxTextureSize) {
                        imageBlob = await this.imageDataToBlob(developedImage.preview);
                    } else {
                        imageData = developedImage.preview;
                        imageBlob = await this.imageDataToBlob(imageData);
                    }
                }

                imageStore.rawDevelopment = {
                    blob: imageBlob,
                    type: imageExtension,
                    values
                }

                await this.saveToStore(imageId, {...imageStore});
            }

            return {
                imageBlob,
                imageExtension,
                imageData,
                values
            }
        },

        async saveToStore(imageId, storeData) {
            if (await window.uploadImagesStore.getItem(imageId)) {
                await window.uploadImagesStore.setItem(imageId, storeData);
                return;
            }

            if (await window.imagesStore.getItem(imageId)) {
                await window.imagesStore.setItem(imageId, storeData);
            }
        },

        async developAndRender() {
            if (!this.$store.state.Projects.image) return;
            if (this.updating && !this.$store.state.UI.compareView.some(item => item.loaded)) return;

            const values = this.getDevelopParameters({
                develop: this.values.develop,
                highlightRecovery: this.values.highlightRecovery,
                linearResponse: this.values.linearResponse,
            });

            if (loader.activeMedia.rawDevelopment.values == values) return;

            this.updating = true;
            gpu.composer.passes[1].enabled = false;
            gpu.getLastPass().enabled = false;
            const imageId = this.$store.state.Projects.image;
            let rawImageData;

            window.UI.showImageLoader(true);
            window.loader.resetActiveMediaRawDeveloped();

            if (this.values.develop) {
                rawImageData = await this.getDevelopedImage(imageId);
            } else {
                rawImageData = await this.getThumbnail(imageId);
            }

            window.loader.setActiveMediaForRaw(rawImageData);

            if (this.$store.state.Projects.image == imageId) {
                await gpu.initNewImage();
            }

            window.UI.hideImageLoader();
            this.updating = false;

            gpu.composer.passes[1].enabled = true;
            gpu.getLastPass().enabled = true;
            if (this.values.develop) window.UI.IDT.selectByName('ACES2065-1');
            else window.UI.IDT.selectByName('sRGB');
            gpu.render();
        },

        async extractThumbnailFromRaw(file, returnBlob = true, customOption = false) {
            let option = "-T";
            if (customOption) option = customOption;

            let result = await this.extractImageFromRaw(file, option);
            if (!returnBlob) return result;

            if (result.file_output) {
                return new Blob([result.file_output], {type: "image/jpeg"});
            }

            return await loader.fetchDefaultThumbnail();
        },

        async extractExif(file) {
            let option = "-T";
            let result = await this.extractImageFromRaw(file, option);
            return result?.exif;
        },

        async extractImageFromRaw(file, options) {
            const reader = new FileReader()
            reader.readAsArrayBuffer(file);

            const data = await new Promise(resolve => {
                reader.onload = () => {
                    resolve(new Uint8Array(reader.result))
                };
            })

            this.worker.postMessage([data, options]);

            const result = await new Promise(resolve => {
                this.worker.onmessage = async e => {
                    if (e.data[1] && e.data[1] == 'error') {
                        this.$snackbar('fylm.ai has encountered a problem processing this Raw file. Please reload the app and try again. If the problem persists please contact us and include the Raw file that triggers the problem.', 'error');
                    }

                    let result = e.data[0]
                    // this.worker.terminate();
                    resolve(result);
                }
            })

            return result
        },

        resetBlock() {
            this.resetValues();
            this.developAndRender();
        }

    }

};


</script>

<style lang="scss" scoped>
.raw-development-block {
    margin-bottom: 5px;
}

.body-container {
    padding: 6px 12px 0;
}

.block-option {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 6px 0;

    span {
        font-size: 0.9rem;
    }

    .dropdown-activator {
        display: flex;
        align-items: center;
        font-size: 0.9rem;

        i {
            font-size: 18px
        }
    }
}
</style>

<style lang="scss">
.raw-option-menu-container {
    border-radius: var(--box-rounding);

    .v-list {
        background: var(--dark-gray);
        padding: 25px 0;

        .v-list-item.v-list-item--link {
            min-height: 20px;
            padding: 0 25px;
            color: var(--font-default-color) !important;
            font-size: 1rem;
            cursor: pointer;

            &:hover {
                background: #363636;
            }
        }
    }
}
</style>
